import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { httpsCallable } from 'firebase/functions';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { functions, db } from '../../firebaseConfig';
import { dataService } from '../../services/dataService';
import { renderMarkdown } from '../../utils/markdown';
import { addLog } from '../../utils/logsStorage';
import { addInboxItem } from '../../utils/inboxStorage';


const callClaude = httpsCallable(functions, 'claude');

// ── Record op parsing ─────────────────────────────────────────────────────────
function extractRecordOps(text) {
  const ops = [];
  const regex = /<RECORD_OP\s+([\s\S]*?)(?:\/\s*>|>([\s\S]*?)<\/RECORD_OP>)/g;
  let m;
  while ((m = regex.exec(text)) !== null) {
    const attrsStr = m[1];
    const body = m[2]?.trim();
    const attrs = {};
    attrsStr.replace(/(\w+)="([^"]*)"/g, (_, k, v) => { attrs[k] = v; });
    if (!attrs.action || !attrs.collectionId) continue;
    try {
      ops.push({ action: attrs.action, collectionId: attrs.collectionId, recordId: attrs.recordId || null, data: body ? JSON.parse(body) : null });
    } catch {}
  }
  return ops;
}

function extractSuggestions(text) {
  const suggestions = [];
  const regex = /<SUGGESTION\s+([\s\S]*?)>([\s\S]*?)<\/SUGGESTION>/g;
  let m;
  while ((m = regex.exec(text)) !== null) {
    const attrsStr = m[1];
    const body = m[2]?.trim();
    const attrs = {};
    attrsStr.replace(/(\w+)="([^"]*)"/g, (_, k, v) => { attrs[k] = v; });
    if (!attrs.collectionId || !body) continue;
    try { suggestions.push({ ...attrs, ...JSON.parse(body) }); } catch {}
  }
  return suggestions;
}

function extractGateRequests(text) {
  const requests = [];
  const regex = /<GATE_REQUEST\s+([\s\S]*?)(?:\/\s*>|>([\s\S]*?)<\/GATE_REQUEST>)/g;
  let m;
  while ((m = regex.exec(text)) !== null) {
    const attrsStr = m[1];
    const body = m[2]?.trim();
    const attrs = {};
    attrsStr.replace(/(\w+)="([^"]*)"/g, (_, k, v) => { attrs[k] = v; });
    if (!attrs.action) continue;
    requests.push({
      gateId: attrs.gateId || null,
      gateLabel: attrs.gateLabel || attrs.gate || 'Approval Gate',
      action: attrs.action,
      description: body || attrs.description || '',
    });
  }
  return requests;
}

function extractInboxMessages(text) {
  const msgs = [];
  const regex = /<INBOX_MESSAGE\s+subject="([^"]*)">([\s\S]*?)<\/INBOX_MESSAGE>/g;
  let m;
  while ((m = regex.exec(text)) !== null) {
    msgs.push({ subject: m[1].trim(), body: m[2].trim() });
  }
  return msgs;
}

function stripSpecialBlocks(text) {
  return text
    .replace(/<RECORD_OP[\s\S]*?(?:\/\s*>|<\/RECORD_OP>)/g, '')
    .replace(/<SUGGESTION[\s\S]*?<\/SUGGESTION>/g, '')
    .replace(/<GATE_REQUEST[\s\S]*?(?:\/\s*>|<\/GATE_REQUEST>)/g, '')
    .replace(/<INBOX_MESSAGE[\s\S]*?<\/INBOX_MESSAGE>/g, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

// ── Suggestion card ───────────────────────────────────────────────────────────
function SuggestionCard({ suggestion, collections, onApply }) {
  const [applied, setApplied] = useState(false);
  const col = collections?.find(c => c.id === suggestion.collectionId);
  return (
    <div className="pm-suggestion-card">
      <div className="pm-suggestion-icon">💡</div>
      <div className="pm-suggestion-body">
        <div className="pm-suggestion-title">
          {suggestion.type === 'add_field'
            ? <>Add <strong>"{suggestion.fieldName}"</strong> field</>
            : 'Pipeline suggestion'}
          {col && <span className="pm-suggestion-col"> to {col.label}</span>}
        </div>
        {suggestion.fieldType && (
          <div className="pm-suggestion-type">
            {suggestion.fieldType}
            {suggestion.options?.length ? ` — ${suggestion.options.join(', ')}` : ''}
          </div>
        )}
        {suggestion.reason && <div className="pm-suggestion-reason">{suggestion.reason}</div>}
        {!applied
          ? <button className="pm-suggestion-apply" onClick={() => { onApply?.(suggestion.collectionId, suggestion); setApplied(true); }}>+ Apply to collection</button>
          : <span className="pm-suggestion-done">✓ Applied</span>
        }
      </div>
    </div>
  );
}

// ── Session helpers ───────────────────────────────────────────────────────────
function mkSession(welcome) {
  return {
    id: `cs_${Date.now()}_${Math.random().toString(36).slice(2, 5)}`,
    title: 'New chat',
    messages: [{ id: 1, role: 'assistant', text: welcome, suggestions: [] }],
    createdAt: new Date().toISOString(),
  };
}

function loadStorage(storageKey) {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return null;
    const data = JSON.parse(raw);
    // Backward compat: old format was a flat messages array
    if (Array.isArray(data)) {
      const sess = { id: 'cs_migrated', title: 'Previous chat', messages: data, createdAt: new Date().toISOString() };
      return { sessions: [sess], currentId: sess.id };
    }
    if (data?.sessions?.length && data.currentId) return data;
    return null;
  } catch { return null; }
}

function saveStorage(storageKey, sessions, currentId) {
  try {
    const trimmed = sessions.slice(-20).map(s => ({ ...s, messages: s.messages.slice(-60) }));
    localStorage.setItem(storageKey, JSON.stringify({ sessions: trimmed, currentId }));
    // Also persist to Firestore so data survives incognito/browser-clear
    if (dataService.userId) {
      const fsKey = storageKey.replace(/\//g, '__');
      setDoc(
        doc(db, 'users', dataService.userId, 'chats', fsKey),
        { sessions: trimmed, currentId, updatedAt: serverTimestamp() }
      ).catch(() => {});
    }
  } catch {}
}

// ── ChatModal — floating window with sessions ─────────────────────────────────
export function ChatModal({ chatKey, modal, initialPos, onClose, onMinimize, workspaceId, onUpdateCollection, onApplySuggestion, onGateRequest }) {
  const { agentName, agentRole, agentInstructions, nodeLabel, playbook, mode, connectedCollections, connectedGates, teamMembers, prefillMessage } = modal;

  const [pos,  setPos]  = useState(() => initialPos || { x: Math.max(20, window.innerWidth - 480), y: Math.max(60, window.innerHeight - 590) });
  const [size, setSize] = useState({ width: 440, height: 560 });
  const [view, setView] = useState('chat');
  const [opsLog, setOpsLog] = useState([]);

  const isDragging  = useRef(false);
  const isResizing  = useRef(false);
  const dragStart   = useRef({});
  const resizeStart = useRef({});
  const bottomRef   = useRef(null);

  useEffect(() => {
    const onMove = (e) => {
      if (isDragging.current) {
        const { mouseX, mouseY, posX, posY } = dragStart.current;
        setPos({ x: Math.max(0, Math.min(window.innerWidth - size.width, posX + e.clientX - mouseX)), y: Math.max(0, Math.min(window.innerHeight - 44, posY + e.clientY - mouseY)) });
      }
      if (isResizing.current) {
        const { mouseX, mouseY, w, h } = resizeStart.current;
        setSize({ width: Math.max(320, w + e.clientX - mouseX), height: Math.max(280, h + e.clientY - mouseY) });
      }
    };
    const onUp = () => { isDragging.current = false; isResizing.current = false; };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    return () => { document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp); };
  }, [size.width]);

  const onDragStart = (e) => {
    if (e.target.closest('button') || e.button !== 0) return;
    e.preventDefault();
    isDragging.current = true;
    dragStart.current = { mouseX: e.clientX, mouseY: e.clientY, posX: pos.x, posY: pos.y };
  };
  const onResizeStart = (e) => {
    e.stopPropagation(); e.preventDefault();
    isResizing.current = true;
    resizeStart.current = { mouseX: e.clientX, mouseY: e.clientY, w: size.width, h: size.height };
  };

  const storageKey = `projectly_chat_${chatKey}`;

  const welcome = mode === 'agent'
    ? `Hi! I'm ${agentName}${agentRole ? `, ${agentRole}` : ''}. How can I help you?`
    : mode === 'direct' ? `You've reached ${nodeLabel}. What do you need?`
    : `Team chat for ${nodeLabel}. What's the update?`;

  const title = mode === 'agent'
    ? `${agentName}${agentRole ? ` · ${agentRole}` : ''}`
    : mode === 'direct' ? nodeLabel
    : `${nodeLabel} team`;

  const initData = useMemo(() => {
    const stored = loadStorage(storageKey);
    if (stored) return { ...stored, hadLocal: true };
    const sess = mkSession(welcome);
    return { sessions: [sess], currentId: sess.id, hadLocal: false };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [sessions,  setSessions]  = useState(initData.sessions);
  const [currentId, setCurrentId] = useState(initData.currentId);
  const [input,     setInput]     = useState(prefillMessage || '');
  const [typing,    setTyping]    = useState(false);
  // fsReady: true once we know the definitive state (either from localStorage or after Firestore load)
  const [fsReady, setFsReady] = useState(initData.hadLocal);

  // On fresh session (no localStorage): load from Firestore
  useEffect(() => {
    if (initData.hadLocal || !dataService.userId) { setFsReady(true); return; }
    const fsKey = storageKey.replace(/\//g, '__');
    getDoc(doc(db, 'users', dataService.userId, 'chats', fsKey))
      .then(snap => {
        if (snap.exists()) {
          const data = snap.data();
          if (data?.sessions?.length && data.currentId) {
            setSessions(data.sessions);
            setCurrentId(data.currentId);
          }
        }
      })
      .catch(() => {})
      .finally(() => setFsReady(true));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const currentSession = sessions.find(s => s.id === currentId) || sessions[0];
  const messages = useMemo(() => currentSession?.messages || [], [currentSession]);

  useEffect(() => {
    if (sessions.length > 0 && fsReady) saveStorage(storageKey, sessions, currentId);
  }, [sessions, currentId, storageKey, fsReady]);

  useEffect(() => {
    if (view === 'chat') bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, typing, view]);

  useEffect(() => {
    const onGateResponse = (e) => {
      if (e.detail.chatKey !== chatKey) return;
      setSessions(ss => ss.map(s => {
        if (s.id !== e.detail.sessionId) return s;
        return { ...s, messages: [...s.messages, { id: Date.now(), role: 'assistant', text: e.detail.message, suggestions: [] }] };
      }));
    };
    window.addEventListener('projectly_gate_response', onGateResponse);
    return () => window.removeEventListener('projectly_gate_response', onGateResponse);
  }, [chatKey]);

  const applyUpdate = (fn) => setSessions(ss => ss.map(s => s.id === currentId ? fn(s) : s));

  const newChat = () => {
    const sess = mkSession(welcome);
    setSessions(ss => [sess, ...ss]);
    setCurrentId(sess.id);
    setView('chat');
    setInput('');
  };

  const openSession = (id) => { setCurrentId(id); setView('chat'); };

  const deleteSession = (id, e) => {
    e.stopPropagation();
    setSessions(ss => {
      const next = ss.filter(s => s.id !== id);
      if (next.length === 0) { const fresh = mkSession(welcome); setCurrentId(fresh.id); return [fresh]; }
      if (id === currentId) setCurrentId(next[0].id);
      return next;
    });
  };

  const tr = (s, n) => s && s.length > n ? s.slice(0, n) + '…' : (s || '');

  const getCollectionsContext = () => {
    if (!connectedCollections?.length) return '';
    const sections = connectedCollections.map(c => {
      const fieldDefs = c.fields.map(f => `  ${f.id} → "${f.name}" (${f.type}${f.options?.length ? ': ' + f.options.join('|') : ''})`).join('\n');
      // Keep only 8 most recent records; truncate long values to 60 chars
      const records = (c.records || []).slice(-8);
      const recordLines = records.map(r => {
        const vals = c.fields.map(f => `${f.id}="${tr(String(r.values?.[f.id] ?? ''), 60)}"`).join(', ');
        return `  [${r.id}] ${vals}`;
      }).join('\n');
      const total = (c.records || []).length;
      const shown = records.length;
      return `Collection: "${c.label}" (id: ${c.id})\nFields:\n${fieldDefs || '  (none)'}\nRecords (${shown} of ${total} shown):\n${recordLines || '  (empty)'}`;
    }).join('\n\n');
    const opsGuide = `\nTo create/update/delete records:\n<RECORD_OP action="create" collectionId="ID">{"field_id": "value"}</RECORD_OP>\n<RECORD_OP action="update" collectionId="ID" recordId="RECORD_ID">{"field_id": "new value"}</RECORD_OP>\n<RECORD_OP action="delete" collectionId="ID" recordId="RECORD_ID" />`;
    return `\n\nDATA COLLECTIONS:\n\n${sections}${opsGuide}`;
  };

  const getTeamContext = () => {
    if (!teamMembers?.length) return '';
    const lines = teamMembers.map(m => `- ${m.name}${m.role ? ` — ${m.role}` : ''}${m.isManager ? ' [Manager]' : ''}${m.instructions ? ` | "${tr(m.instructions, 80)}"` : ''}`).join('\n');
    return `\n\nYOUR TEAM:\n${lines}`;
  };

  const getGatesContext = () => {
    if (!connectedGates?.length) return '';
    const lines = connectedGates.map(g =>
      `- "${g.label}" (id: ${g.id})${g.approver ? ` — Approver: ${g.approver}` : ''}${g.criteria ? ` — Criteria: ${g.criteria}` : ''}`
    ).join('\n');
    return `\n\nAPPROVAL GATES:\nWhen your work requires human sign-off before proceeding, submit a gate request. Available gates:\n${lines}\n\nTo request approval:\n<GATE_REQUEST gateId="GATE_ID" gateLabel="Gate Name" action="Brief description of what needs approval" description="Optional detail" />\nOnly submit a gate request when work is fully prepared and ready for human review.`;
  };

  // Ref so send() (memoized with useCallback) always calls the latest version of
  // getSystemPrompt rather than the stale one from the render it was created in.
  const getSystemPromptRef = useRef(null);

  const getSystemPrompt = () => {
    const collectionsCtx = getCollectionsContext();
    const teamCtx = getTeamContext();
    const gatesCtx = getGatesContext();
    const agentSimGuide = `\n\nAGENT COMMUNICATION MODEL:\nAI agents respond synchronously and instantly — never say you are "waiting to hear back." When you assign work to a team member, immediately simulate their response. If an assigned agent has no backend API access, they should say so honestly. Report back what each agent said.`;
    const suggestionGuide = connectedCollections?.length ? `\n\nPIPELINE IMPROVEMENT SUGGESTIONS:\nWhen you notice a recurring pattern the schema doesn't handle well, suggest one specific improvement:\n<SUGGESTION collectionId="COLLECTION_ID" type="add_field">{"fieldName": "Category", "fieldType": "select", "options": ["A", "B"], "reason": "data-driven reason"}</SUGGESTION>` : '';
    const inboxMsgGuide = `\n\nMESSAGING THE USER (built-in platform feature):\nYou have the ability to send the user structured messages — reports, summaries, tables, counts — directly to their inbox. This is a built-in platform capability: the system intercepts the XML tag below and delivers it automatically. No external email account or service is required. You MUST use this when the user asks for a report, summary, data retrieval, or anything worth keeping. Never say you cannot send messages or reports.\n\nUsage:\n<INBOX_MESSAGE subject="Descriptive subject line">\nMessage body. Markdown supported — use tables, bold text, bullet lists.\n</INBOX_MESSAGE>`;
    // Remind Claude that the live DATA COLLECTIONS section is always authoritative —
    // prevents it from anchoring on stale values it stated in earlier conversation turns.
    const dataAuthorityNote = collectionsCtx ? `\n\nIMPORTANT: The DATA COLLECTIONS section above always reflects the current live state of records, including any updates made during this conversation. If it contradicts something you said in an earlier message, always trust the DATA COLLECTIONS — not your earlier statements.` : '';
    if (mode === 'agent') return `You are ${agentName}, an AI agent${agentRole ? ` with the role of "${agentRole}"` : ''}${nodeLabel ? ` working within the ${nodeLabel} function` : ''}.\n${agentInstructions ? `\nYour instructions:\n${tr(agentInstructions, 600)}` : ''}\n${playbook ? `\nFunction playbook:\n${tr(playbook, 600)}` : ''}${teamCtx}${collectionsCtx}${gatesCtx}${agentSimGuide}${suggestionGuide}${inboxMsgGuide}${dataAuthorityNote}\n\nRespond in character as ${agentName}. Be proactive and decisive. Keep responses concise and professional. When asked for reports or summaries, always use INBOX_MESSAGE — never claim you cannot deliver information to the user.`;
    if (mode === 'team') {
      const teamRoster = teamMembers?.length ? `\n\nTEAM:\n${teamMembers.map(m => `- ${m.name}${m.role ? ` (${m.role})` : ''}${m.isManager ? ' [Manager]' : ''}${m.instructions ? `: "${tr(m.instructions, 80)}"` : ''}`).join('\n')}` : '';
      return `You are the manager/coordinator for the "${nodeLabel}" function.\n${playbook ? `\nPlaybook:\n${tr(playbook, 600)}` : ''}${teamRoster}${collectionsCtx}${gatesCtx}${agentSimGuide}${suggestionGuide}${inboxMsgGuide}${dataAuthorityNote}\n\nBe decisive and action-oriented. Use tables for status overviews. When asked to assign or process items, do it via RECORD_OPs and simulate team responses instantly.`;
    }
    return `You are ${agentName || nodeLabel}${agentRole ? `, ${agentRole}` : ''}, part of the "${nodeLabel}" team.${playbook ? `\n\nTeam playbook:\n${tr(playbook, 400)}` : ''}${collectionsCtx}${inboxMsgGuide}${dataAuthorityNote}\n\nHelp the user concisely and professionally. When asked for reports or summaries, always use INBOX_MESSAGE — never claim you cannot deliver information.`;
  };
  // Keep ref current every render so send() always uses the latest collections context.
  getSystemPromptRef.current = getSystemPrompt;

  const send = useCallback(async () => {
    const text = input.trim();
    if (!text || typing) return;
    setInput('');

    let currentMsgs = [];
    applyUpdate(s => {
      currentMsgs = s.messages;
      return {
        ...s,
        title: s.title === 'New chat' ? text.slice(0, 48) : s.title,
        messages: [...s.messages, { id: Date.now(), role: 'user', text, suggestions: [] }],
      };
    });
    setTyping(true);

    addLog({ type: 'chat', nodeLabel, agentName: agentName || nodeLabel || 'User', detail: `Message sent: ${text.slice(0, 120)}${text.length > 120 ? '…' : ''}` });
    try {
      const fullHistory = [...currentMsgs, { role: 'user', text }];
      const firstUserIdx = fullHistory.findIndex(m => m.role === 'user');
      const apiMessages = fullHistory.slice(firstUserIdx).slice(-8).map(m => ({ role: m.role, content: m.text }));
      const result = await callClaude({ messages: apiMessages, model: 'claude-haiku-4-5-20251001', system: getSystemPromptRef.current(), max_tokens: 700, workspaceId: workspaceId || 'unknown', purpose: 'agent_chat' });
      const raw = result.data?.content?.[0]?.text ?? 'Sorry, I could not respond right now.';
      const ops = extractRecordOps(raw);
      const suggestions = extractSuggestions(raw);
      const gateReqs = extractGateRequests(raw);
      const inboxMsgs = extractInboxMessages(raw);
      const displayText = stripSpecialBlocks(raw);
      if (ops.length && onUpdateCollection) {
        const log = [];
        for (const op of ops) {
          onUpdateCollection(op.collectionId, op);
          const label = op.action === 'create' ? 'Created record' : op.action === 'update' ? 'Updated record' : 'Deleted record';
          log.push(label);
          addLog({ type: 'record_op', nodeLabel, agentName: agentName || 'Agent', detail: `${label} in ${op.collectionId}${op.recordId ? ` (${op.recordId})` : ''}` });
        }
        setOpsLog(prev => [...prev, ...log]);
      }
      if (gateReqs.length && onGateRequest) {
        const log = [];
        for (const req of gateReqs) {
          onGateRequest(req.gateId, {
            action: req.action,
            description: req.description,
            gateLabel: req.gateLabel,
            agentName: agentName || nodeLabel,
            nodeLabel,
            chatKey,
            sessionId: currentId,
          });
          log.push(`⏳ Approval requested: ${req.action}`);
        }
        setOpsLog(prev => [...prev, ...log]);
      }
      if (inboxMsgs.length) {
        for (const msg of inboxMsgs) {
          addInboxItem({
            id: `inbox_agent_${Date.now()}_${Math.random().toString(36).slice(2, 5)}`,
            type: 'notification',
            subject: msg.subject,
            from: agentName || nodeLabel || 'Agent',
            fromNode: nodeLabel || '',
            preview: msg.body.slice(0, 120),
            body: msg.body,
            timestamp: new Date().toISOString(),
            read: false, starred: false, folder: 'inbox',
            workspaceId: workspaceId || null,
          });
          addLog({ type: 'chat', nodeLabel, agentName: agentName || nodeLabel || 'Agent', detail: `Inbox message sent: ${msg.subject}` });
        }
        setOpsLog(prev => [...prev, ...inboxMsgs.map(m => `📥 Sent to inbox: ${m.subject}`)]);
      }
      setTyping(false);
      applyUpdate(s => ({ ...s, messages: [...s.messages, { id: Date.now() + 1, role: 'assistant', text: displayText, suggestions }] }));
    } catch (err) {
      setTyping(false);
      addLog({ type: 'error', nodeLabel, agentName: agentName || nodeLabel || 'Agent', detail: `API error: ${err?.message || 'Unknown error'}` });
      applyUpdate(s => ({ ...s, messages: [...s.messages, { id: Date.now() + 1, role: 'assistant', text: `Error: ${err?.message || 'Unknown error'}`, suggestions: [] }] }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input, typing, currentId, workspaceId, onUpdateCollection, onGateRequest]);

  return (
    <div className="float-win pm-chat-win" style={{ left: pos.x, top: pos.y, width: size.width, height: size.height }}>
      <div className="float-win-header" onMouseDown={onDragStart}>
        <span className="float-win-icon">💬</span>
        <span className="float-win-title">{title}</span>
        {connectedCollections?.length > 0 && (
          <span className="pm-collections-badge" title={connectedCollections.map(c => c.label).join(', ')}>📂{connectedCollections.length}</span>
        )}
        <div className="float-win-actions">
          <button className="float-btn" onClick={() => setView(v => v === 'sessions' ? 'chat' : 'sessions')} title="Chat history" style={view === 'sessions' ? { background: 'var(--bg-hover)' } : {}}>🕐</button>
          <button className="float-btn" onClick={newChat} title="New chat">✚</button>
          <button className="float-btn float-btn-minimize" onClick={onMinimize} title="Minimize">−</button>
          <button className="float-btn float-btn-close" onClick={onClose} title="Close">✕</button>
        </div>
      </div>

      {opsLog.length > 0 && (
        <div className="pm-ops-log">{opsLog.map((l, i) => <span key={i} className="pm-ops-item">✓ {l}</span>)}</div>
      )}

      {view === 'sessions' && (
        <div className="pm-sessions-panel">
          <div className="pm-sessions-hd">
            <span className="pm-sessions-title">Chat history · {sessions.length} conversation{sessions.length !== 1 ? 's' : ''}</span>
            <button className="pm-new-sess-btn" onClick={newChat}>+ New</button>
          </div>
          <div className="pm-sessions-list">
            {sessions.map(s => (
              <div key={s.id} className={`pm-sess-item${s.id === currentId ? ' active' : ''}`} onClick={() => openSession(s.id)}>
                <div className="pm-sess-main">
                  <div className="pm-sess-title">{s.title}</div>
                  <div className="pm-sess-meta">
                    {new Date(s.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                    {' · '}{s.messages.length} msg{s.messages.length !== 1 ? 's' : ''}
                  </div>
                </div>
                <button className="pm-sess-del" title="Delete" onClick={(e) => deleteSession(s.id, e)}>🗑</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {view === 'chat' && (
        <>
          <div className="pm-chat-body">
            {messages.map(m => (
              <div key={m.id} className={`pm-msg ${m.role === 'user' ? 'pm-msg-me' : 'pm-msg-them'}`}>
                {m.role === 'assistant'
                  ? renderMarkdown(m.text)
                  : m.text.split('\n').map((line, i) => <p key={i} style={{ margin: i === 0 ? 0 : '4px 0 0' }}>{line}</p>)
                }
                {m.suggestions?.map((s, i) => (
                  <SuggestionCard key={i} suggestion={s} collections={connectedCollections} onApply={onApplySuggestion} />
                ))}
              </div>
            ))}
            {typing && <div className="pm-msg pm-msg-them pm-typing"><span /><span /><span /></div>}
            <div ref={bottomRef} />
          </div>
          <div className="pm-chat-input-row">
            <input className="pm-chat-input" placeholder="Type a message…" value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
              autoFocus
            />
            <button className="pm-send-btn" onClick={send} disabled={!input.trim() || typing}>Send</button>
          </div>
        </>
      )}

      <div className="float-resize-handle" onMouseDown={onResizeStart} />
    </div>
  );
}

// ── TasksModal ────────────────────────────────────────────────────────────────
export function TasksModal({ modal, onClose, onSaveTasks }) {
  const { nodeLabel, raiseMode, tasks: initialTasks = [] } = modal;
  const [tasks,   setTasks]   = useState(initialTasks);
  const [newTask, setNewTask] = useState('');
  const inputRef = useRef(null);

  useEffect(() => { if (raiseMode) setTimeout(() => inputRef.current?.focus(), 50); }, [raiseMode]);

  const updateAndSave = (next) => { setTasks(next); onSaveTasks?.(next); };
  const toggle = (id) => updateAndSave(tasks.map(t => t.id === id ? { ...t, done: !t.done } : t));
  const remove = (id) => updateAndSave(tasks.filter(t => t.id !== id));
  const addTask = () => {
    const text = newTask.trim(); if (!text) return;
    updateAndSave([...tasks, { id: `t${Date.now()}`, text, done: false, createdAt: new Date().toISOString() }]);
    setNewTask('');
  };

  const doneTasks    = tasks.filter(t => t.done);
  const pendingTasks = tasks.filter(t => !t.done);

  return (
    <div className="pm-backdrop" onClick={onClose}>
      <div className="pm-modal pm-tasks" onClick={e => e.stopPropagation()}>
        <div className="pm-header">
          <span className="pm-title">📋 Tasks — {nodeLabel}</span>
          <span className="pm-task-count">{pendingTasks.length} pending · {doneTasks.length} done</span>
          <button className="pm-close" onClick={onClose}>✕</button>
        </div>
        <div className="pm-tasks-body">
          {tasks.length === 0 && <div className="pm-no-tasks">No tasks yet. Add one below.</div>}
          {pendingTasks.map(t => (
            <div key={t.id} className="pm-task-row">
              <input type="checkbox" checked={false} onChange={() => toggle(t.id)} className="pm-task-check" />
              <span className="pm-task-text">{t.text}</span>
              <button className="pm-task-del" onClick={() => remove(t.id)}>✕</button>
            </div>
          ))}
          {doneTasks.length > 0 && <div className="pm-tasks-divider">Completed</div>}
          {doneTasks.map(t => (
            <div key={t.id} className="pm-task-row pm-task-done">
              <input type="checkbox" checked={true} onChange={() => toggle(t.id)} className="pm-task-check" />
              <span className="pm-task-text">{t.text}</span>
              <button className="pm-task-del" onClick={() => remove(t.id)}>✕</button>
            </div>
          ))}
        </div>
        <div className="pm-task-add-row">
          <input ref={inputRef} className="pm-task-input"
            placeholder={raiseMode ? 'Describe the task to raise…' : 'Add a task…'}
            value={newTask} onChange={e => setNewTask(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && addTask()}
          />
          <button className="pm-add-btn" onClick={addTask} disabled={!newTask.trim()}>
            {raiseMode ? 'Raise' : 'Add'}
          </button>
        </div>
      </div>
    </div>
  );
}
